/** Property Profile + Sponsor / Developer renderers — shared by the demo
 *  profile dialog and the live Deal Dossier tabs. Pure presentation: every
 *  value comes from the profile object; the caller decides whether that is a
 *  curated demo fixture or a real record mapped field-for-field. */
import type React from 'react'
import {
  MapPin, Building2, Download, Linkedin, Globe, FileText,
  Users, Phone, Mail, Star,
  CheckCircle2, AlertTriangle, Info,
} from 'lucide-react'
import type { PropertyProfile } from '@/types/propertyProfile'
import { chartProgressGradient, chartSeriesColor } from '@/config/chartTheme'

export interface PortfolioProperty {
  name: string
  type: string
  location: string
  units?: number
  sf?: number
  value: string
  year: number
  status: string
}

export interface DealHistory {
  dealId: string
  property: string
  amount: string
  date: string
  rate: string
  status: string
  revenue: string
}

export interface Principal {
  name: string
  title: string
  experience: string
  previous: string[]
  education: string
  boards: string[]
}

export interface NewsItem {
  headline: string
  timeAgo: string
}

export interface RiskFactor {
  factor: string
  level: 'LOW' | 'MODERATE' | 'NONE' | 'EXCELLENT'
  icon: 'check' | 'warn' | 'info'
}

export interface SponsorProfile {
  id: string
  companyName: string
  principal: string
  founded: number
  hq: string
  aum: string
  propertyCount: number
  totalUnits: string
  reputationScore: number
  // Antarctica relationship
  dealsWithAntarctica: number
  totalVolume: string
  revenueEarned: string
  avgDscrAtOrig: string
  defaultHistory: string
  avgDaysToClose: number
  firstDeal: string
  relationshipTier: string
  dealHistory: DealHistory[]
  // Risk
  riskFactors: RiskFactor[]
  overallRiskRating: string
  overallRiskScore: number
  concentrationNote: string
  // Portfolio
  portfolio: PortfolioProperty[]
  portfolioMetrics: string
  geoBreakdown: string
  typeBreakdown: string
  // Principals
  principals: Principal[]
  // News
  news: NewsItem[]
  // Financial strength
  netWorth: string
  liquidity: string
  leverageRatio: string
  creditLines: string
  guarantorCapacity: string
  taxReturns: string
  pfs: string
}



const PanelSection = ({ title, children, className = '' }: { title: string; children: React.ReactNode; className?: string }) => (
  <div className={`rounded-lg border border-amber-400/10 bg-black/30 ${className}`}>
    <div className="px-3 py-2 border-b border-amber-400/10">
      <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-stone-500">{title}</span>
    </div>
    <div className="p-3">{children}</div>
  </div>
)

const MetricBox = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-md border border-amber-400/8 bg-black/30 px-3 py-2 text-center">
    <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-stone-500">{label}</div>
    <div className="text-[15px] font-semibold text-stone-100 mt-0.5">{value}</div>
  </div>
)

const RiskBadge = ({ level }: { level: string }) => {
  const color = level === 'LOW' || level === 'NONE' || level === 'EXCELLENT'
    ? 'text-emerald-400'
    : level === 'MODERATE'
      ? 'text-amber-400'
      : 'text-red-400'
  const icon = level === 'LOW' || level === 'NONE' || level === 'EXCELLENT'
    ? <CheckCircle2 size={11} className="inline -mt-0.5" />
    : level === 'MODERATE'
      ? <AlertTriangle size={11} className="inline -mt-0.5" />
      : <Info size={11} className="inline -mt-0.5" />
  return <span className={`${color} text-[11px] font-medium flex items-center gap-1`}>{icon} {level}</span>
}

const ScoreBar = ({ score, max = 100 }: { score: number; max?: number }) => {
  const pct = (score / max) * 100
  return (
    <div className="flex items-center gap-2">
      <div className="chart-viz-track chart-viz-bar--rounded h-2 flex-1">
        <div
          className="chart-viz-bar chart-viz-bar--rounded h-full"
          style={{ width: `${pct}%`, background: chartProgressGradient(0) }}
        />
      </div>
      <span className="text-[12px] font-mono" style={{ color: chartSeriesColor(0) }}>{score}/{max}</span>
    </div>
  )
}

/** Distinct bar colors by period bucket; taller bar = larger % of roll (weighting). */
const LEASE_EXPIRATION_BAR_BG: string[] = [
  chartSeriesColor(0),
  chartSeriesColor(2),
  chartSeriesColor(4),
  chartSeriesColor(6),
]

function RentRollMetricsFooter({ profile }: { profile: PropertyProfile }) {
  return (
    <div className="space-y-1 text-[12px] mt-3 pt-3 border-t border-amber-400/10">
      {profile.avgRentUnit && (
        <p><span className="text-stone-500">Avg Rent/Unit:</span> <span className="text-stone-200">{profile.avgRentUnit}</span></p>
      )}
      {profile.avgRentSF && (
        <p><span className="text-stone-500">Avg Rent/SF:</span> <span className="text-stone-200">{profile.avgRentSF}</span></p>
      )}
      {profile.leaseRenewalRate && (
        <p><span className="text-stone-500">Lease Renewal Rate:</span> <span className="text-stone-200">{profile.leaseRenewalRate}</span></p>
      )}
      {profile.avgLeaseTerm && (
        <p><span className="text-stone-500">Avg Lease Term:</span> <span className="text-stone-200">{profile.avgLeaseTerm}</span></p>
      )}
      {profile.concessions && (
        <p><span className="text-stone-500">Concessions:</span> <span className="text-stone-200">{profile.concessions}</span></p>
      )}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   TAB: PROPERTY PROFILE
   ═══════════════════════════════════════════════════════════════════════════ */

export function PropertyTab({ profile }: { profile: PropertyProfile }) {
  return (
    <div className="space-y-3 pb-6">
      {/* ── HEADER ──────────────────────────────────────── */}
      <div className="rounded-lg border border-amber-400/15 bg-gradient-to-r from-amber-400/[0.04] to-transparent p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Building2 size={18} className="text-amber-300" />
              <h2 className="text-xl font-bold text-stone-50">{profile.propertyName}</h2>
            </div>
            <p className="text-[13px] text-stone-300">{profile.tagline} | {profile.location}</p>
            <p className="text-[12px] text-stone-400 mt-1">
              {profile.loanAmount} {profile.product} | {profile.dealId} | Stage: {profile.stage}
            </p>
          </div>
          <div className="flex gap-2">
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(profile.fullAddress)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[11px] text-amber-300 border border-amber-400/20 rounded px-2 py-1 hover:bg-amber-400/10 transition-colors"
            >
              <MapPin size={11} /> Google Maps
            </a>
            <button className="flex items-center gap-1 text-[11px] text-amber-300 border border-amber-400/20 rounded px-2 py-1 hover:bg-amber-400/10 transition-colors">
              <Download size={11} /> Download Package
            </button>
          </div>
        </div>
      </div>

      {/* ── PHOTOS (left) + OVERVIEW & MAP (right) ──────── */}
      <div className="grid grid-cols-2 gap-3">
        <PanelSection title="Property Photos">
          {profile.photos.length === 0 && (
            <p className="px-1 py-6 text-center text-[11px] text-stone-500">
              No photos on record — attach property photos in the deal file to feature them here.
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            {profile.photos.map((photo, i) => (
              <div
                key={i}
                className="flex flex-col rounded border border-amber-400/10 overflow-hidden bg-stone-900/60 shadow-sm"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className="h-full w-full object-cover transition-transform duration-300 hover:scale-[1.03]"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <p className="text-[10px] text-stone-500 leading-tight px-2 py-1.5 border-t border-amber-400/10">
                  {photo.alt}
                </p>
              </div>
            ))}
          </div>
        </PanelSection>

        <PanelSection title="Property Overview" className="flex flex-col min-h-0">
          <div className="space-y-2 text-[12px]">
            {[
              ['Property Type', profile.propertyType],
              ['Year Built', profile.yearBuilt ? String(profile.yearBuilt) : 'Not on record'],
              ...(profile.totalUnits ? [['Total Units', String(profile.totalUnits)]] : []),
              ['Total SF', profile.totalSF],
              ...(profile.avgUnitSize ? [['Avg Unit Size', profile.avgUnitSize]] : []),
              ['Stories', profile.stories ? String(profile.stories) : 'Not on record'],
              ['Parking', profile.parking],
              ['Amenities', profile.amenities],
              ['Zoning', profile.zoning],
              ['Parcel', profile.parcel],
              ['Last Sale', profile.lastSale],
            ].map(([label, val]) => (
              <div key={label} className="flex items-start gap-2">
                <span className="text-stone-500 flex-shrink-0 w-[100px]">{label}:</span>
                <span className="text-stone-200">{val}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-col flex-1 min-h-[180px]">
            <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-stone-500 mb-1.5">Location Map</div>
            <div className="rounded border border-amber-400/10 overflow-hidden flex-1 min-h-[180px]">
              {profile.mapLat !== 0 || profile.mapLng !== 0 ? (
                <iframe
                  title="Property Map"
                  width="100%"
                  height="100%"
                  className="min-h-[180px]"
                  style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) brightness(0.9) contrast(1.1)' }}
                  loading="lazy"
                  src={`https://maps.google.com/maps?q=${profile.mapLat},${profile.mapLng}&z=15&output=embed`}
                />
              ) : profile.fullAddress && profile.fullAddress !== '—' ? (
                <iframe
                  title="Property Map"
                  width="100%"
                  height="100%"
                  className="min-h-[180px]"
                  style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) brightness(0.9) contrast(1.1)' }}
                  loading="lazy"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(profile.fullAddress)}&z=15&output=embed`}
                />
              ) : (
                <p className="min-h-[180px] flex items-center justify-center text-[11px] text-stone-500">
                  No address on record — no map to show.
                </p>
              )}
            </div>
          </div>
        </PanelSection>
      </div>

      {/* ── NEIGHBORHOOD & DEMOGRAPHICS ────────────────── */}
      <PanelSection title="Neighborhood & Demographics">
        <div className="grid grid-cols-6 gap-2 mb-3">
          <MetricBox label="Pop (3mi)" value={profile.population3mi} />
          <MetricBox label="Med HHI" value={profile.medianHHI} />
          <MetricBox label="Pop Growth" value={profile.popGrowth} />
          <MetricBox label="Walk Score" value={profile.walkScore} />
          <MetricBox label="Crime Idx" value={profile.crimeIndex} />
          <MetricBox label="Emp Growth" value={profile.empGrowth} />
        </div>
        <div className="space-y-1.5 text-[12px]">
          <p><span className="text-stone-500">Top Employers (5mi):</span> <span className="text-stone-200">{profile.topEmployers.length ? profile.topEmployers.join(', ') : 'Not on record'}</span></p>
          <p><span className="text-stone-500">Schools:</span> <span className="text-stone-200">{profile.schools}</span> <span className="text-stone-500 mx-2">|</span> <span className="text-stone-500">Transit:</span> <span className="text-stone-200">{profile.transit}</span></p>
          <p><span className="text-stone-500">Nearby Developments:</span> <span className="text-stone-200">{profile.nearbyDevelopments}</span></p>
          <p><span className="text-stone-500">Submarket:</span> <span className="text-stone-200">{profile.submarket}</span> <span className="text-stone-500 mx-2">|</span> <span className="text-stone-500">Vacancy:</span> <span className="text-stone-200">{profile.submarketVacancy}</span> <span className="text-stone-500 mx-2">|</span> <span className="text-stone-500">Rent Growth:</span> <span className="text-stone-200">{profile.submarketRentGrowth}</span></p>
        </div>
      </PanelSection>

      {/* ── FINANCIAL PERFORMANCE + RENT ROLL ────────────── */}
      <div className="grid grid-cols-2 gap-3">
        <PanelSection title="Financial Performance">
          <div className="space-y-2 text-[12px]">
            {[
              ['Revenue', profile.revenue, false],
              ['Expenses', profile.expenses, false],
              ['NOI', profile.noi, true],
              ['Cap Rate', profile.capRate, false],
              ['Occupancy', profile.occupancy, true],
            ].map(([label, val, bold]) => (
              <div key={label as string} className="flex justify-between items-center">
                <span className="text-stone-400">{label as string}</span>
                <span className={bold ? 'text-amber-300 font-semibold' : 'text-stone-200'}>{val as string}</span>
              </div>
            ))}
            <div className="h-px bg-amber-400/10 my-1" />
            {[
              ['Expense Ratio', profile.expenseRatio],
              ['Rev/Unit', profile.revPerUnit],
              ['Exp/Unit', profile.expPerUnit],
              ['NOI/Unit', profile.noiPerUnit],
            ].map(([label, val]) => (
              <div key={label} className="flex justify-between items-center">
                <span className="text-stone-500">{label}</span>
                <span className="text-stone-300 font-mono text-[11px]">{val}</span>
              </div>
            ))}
          </div>
        </PanelSection>

        <PanelSection title="Rent Roll Summary">
          {profile.unitMix ? (
            <div>
              <div className="text-[10px] font-mono text-stone-500 uppercase tracking-wider mb-2">Unit Mix</div>
              <table className="dense-table">
                <thead>
                  <tr>
                    <th>Unit Type</th>
                    <th className="text-right">Avg Rent</th>
                    <th className="text-right">Occ</th>
                  </tr>
                </thead>
                <tbody>
                  {profile.unitMix.map((u) => (
                    <tr key={u.type}>
                      <td>{u.type}</td>
                      <td className="text-right font-mono">${u.avgRent.toLocaleString()}/mo</td>
                      <td className="text-right font-mono">{u.occupancy}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <RentRollMetricsFooter profile={profile} />
            </div>
          ) : profile.tenants ? (
            <div>
              <table className="dense-table">
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Suite</th>
                    <th className="text-right">SF</th>
                    <th className="text-right">Rent/Mo</th>
                    <th>Lease End</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {profile.tenants.map((t) => (
                    <tr key={t.tenant + t.suite}>
                      <td className={t.status === 'Vacant' ? 'text-red-400' : ''}>{t.tenant}</td>
                      <td>{t.suite}</td>
                      <td className="text-right font-mono">{t.sf.toLocaleString()}</td>
                      <td className="text-right font-mono">{t.rentMo > 0 ? `$${t.rentMo.toLocaleString()}` : '--'}</td>
                      <td>{t.leaseEnd}</td>
                      <td><span className={`text-[10px] px-1.5 py-0.5 rounded ${t.status === 'Vacant' ? 'bg-red-400/10 text-red-400' : 'bg-emerald-400/10 text-emerald-400'}`}>{t.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <RentRollMetricsFooter profile={profile} />
            </div>
          ) : (
            <p className="text-[12px] text-stone-500">Rent roll summary unavailable.</p>
          )}
        </PanelSection>
      </div>

      {/* ── LEASE EXPIRATION SCHEDULE ─────────────────────── */}
      <PanelSection title="Lease Expiration Schedule">
        {profile.leaseExpirations.length === 0 && (
          <p className="py-4 text-center text-[11px] text-stone-500">No lease schedule on record.</p>
        )}
        {profile.leaseExpirations.length > 0 && (() => {
          const maxPct = Math.max(...profile.leaseExpirations.map((l) => l.pctOfTotal), 1)
          return (
            <div className="flex items-end gap-2 min-h-[104px] pt-1">
              {profile.leaseExpirations.map((le, i) => (
                <div key={le.year} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                  <span className="text-[10px] font-mono text-stone-300 tabular-nums">{le.pctOfTotal}%</span>
                  <div
                    className="w-full rounded-t-md shadow-sm border border-stone-700/40 transition-all"
                    title={`${le.year}: ${le.pctOfTotal}% of roll (${le.sf.toLocaleString()} SF)`}
                    style={{
                      height: `${Math.max(14, (le.pctOfTotal / maxPct) * 88)}px`,
                      background: LEASE_EXPIRATION_BAR_BG[i % LEASE_EXPIRATION_BAR_BG.length],
                    }}
                  />
                  <span className="text-[9px] text-stone-500 text-center leading-tight px-0.5">{le.year}</span>
                </div>
              ))}
            </div>
          )
        })()}
      </PanelSection>

      {/* ── CONTACTS + COMPS ─────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3">
        <PanelSection title="Property Contacts">
          <div className="space-y-3">
            {profile.contacts.map((c) => (
              <div key={c.role} className="text-[12px]">
                <div className="text-[10px] font-mono uppercase tracking-wider text-stone-500 mb-0.5">{c.role}</div>
                <div className="text-stone-200 font-medium">{c.name}, {c.company}</div>
                <div className="flex items-center gap-3 text-stone-400 mt-0.5">
                  <span className="flex items-center gap-1"><Phone size={10} /> {c.phone}</span>
                  <span className="flex items-center gap-1"><Mail size={10} /> {c.email}</span>
                </div>
              </div>
            ))}
          </div>
        </PanelSection>

        <PanelSection title="Comparable Properties">
          {profile.comparables.length === 0 && (
            <p className="py-4 text-center text-[11px] text-stone-500">No comparable set on record.</p>
          )}
          <div className="space-y-2">
            {profile.comparables.map((comp, i) => (
              <div key={comp.name} className="flex items-start justify-between text-[12px] pb-2 border-b border-amber-400/6 last:border-0 last:pb-0">
                <div>
                  <span className="text-stone-500 mr-1">Comp {i + 1}:</span>
                  <span className="text-stone-200">{comp.name}</span>
                  <div className="text-stone-400 text-[11px]">
                    {comp.units ? `${comp.units} units` : comp.sf ? `${comp.sf.toLocaleString()} SF` : ''} | ${typeof comp.avgRent === 'number' && comp.avgRent > 100 ? `$${comp.avgRent} ADR` : `$${comp.avgRent.toLocaleString()}${comp.units ? '/mo' : '/SF'}`} | {comp.capRate}% cap
                  </div>
                </div>
                <span className="text-[10px] text-stone-500">{comp.distance}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 pt-2 border-t border-amber-400/10">
            <p className="text-[11px] text-stone-400">{profile.compSummary}</p>
          </div>
        </PanelSection>
      </div>

      {/* ── INSPECTION & CONDITION ──────────────────────── */}
      <PanelSection title="Inspection & Condition">
        <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-[12px]">
          <p><span className="text-stone-500">Last Inspection:</span> <span className="text-stone-200">{profile.lastInspection}</span></p>
          <p><span className="text-stone-500">Inspector:</span> <span className="text-stone-200">{profile.inspector}</span></p>
          <p><span className="text-stone-500">Overall Condition:</span> <span className="text-amber-300 font-medium">{profile.overallCondition}</span></p>
          <p><span className="text-stone-500">Immediate Repairs:</span> <span className="text-stone-200">{profile.immediateRepairs}</span></p>
          <p><span className="text-stone-500">Deferred Maintenance:</span> <span className="text-stone-200">{profile.deferredMaintenance}</span></p>
          <p><span className="text-stone-500">Environmental:</span> <span className="text-stone-200">{profile.environmental}</span></p>
          <p><span className="text-stone-500">Seismic Zone:</span> <span className="text-stone-200">{profile.seismicZone}</span></p>
          <p><span className="text-stone-500">Flood Zone:</span> <span className="text-stone-200">{profile.floodZone}</span></p>
        </div>
      </PanelSection>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   TAB: SPONSOR / DEVELOPER PROFILE
   ═══════════════════════════════════════════════════════════════════════════ */

export function SponsorTab({ sponsor }: { sponsor: SponsorProfile }) {
  return (
    <div className="space-y-3 pb-6">
      {/* ── SPONSOR HEADER ──────────────────────────────── */}
      <div className="rounded-lg border border-amber-400/15 bg-gradient-to-r from-amber-400/[0.04] to-transparent p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Users size={18} className="text-amber-300" />
              <h2 className="text-xl font-bold text-stone-50">{sponsor.companyName}</h2>
            </div>
            <p className="text-[13px] text-stone-300">
              Principal: {sponsor.principal} | Founded: {sponsor.founded || 'Not on record'} | HQ: {sponsor.hq}
            </p>
            <p className="text-[12px] text-stone-400 mt-1">
              AUM: {sponsor.aum} | Properties: {sponsor.propertyCount || 'Not on record'} | Units: {sponsor.totalUnits}
            </p>
          </div>
          <div className="flex gap-2">
            <button className="flex items-center gap-1 text-[11px] text-amber-300 border border-amber-400/20 rounded px-2 py-1 hover:bg-amber-400/10 transition-colors">
              <Linkedin size={11} /> LinkedIn
            </button>
            <button className="flex items-center gap-1 text-[11px] text-amber-300 border border-amber-400/20 rounded px-2 py-1 hover:bg-amber-400/10 transition-colors">
              <Globe size={11} /> Website
            </button>
            <button className="flex items-center gap-1 text-[11px] text-amber-300 border border-amber-400/20 rounded px-2 py-1 hover:bg-amber-400/10 transition-colors">
              <FileText size={11} /> SEC Filings
            </button>
          </div>
        </div>
        <div className="mt-3">
          {sponsor.reputationScore > 0 ? (
            <>
              <div className="flex items-center gap-3 mb-1">
                <span className="text-[11px] text-stone-500">Reputation Score:</span>
                <span className="text-[13px] font-bold text-amber-300">{sponsor.reputationScore}/100</span>
                <span className="text-[11px] text-emerald-400 font-medium uppercase">{sponsor.reputationScore >= 90 ? 'Excellent' : sponsor.reputationScore >= 80 ? 'Good' : 'Adequate'}</span>
              </div>
              <ScoreBar score={sponsor.reputationScore} />
            </>
          ) : (
            <p className="text-[11px] text-stone-500">Reputation Score: <span className="text-stone-400">not yet assessed — no score is displayed until one is recorded.</span></p>
          )}
        </div>
      </div>

      {/* ── RELATIONSHIP + RISK ASSESSMENT ──────────────── */}
      <div className="grid grid-cols-2 gap-3">
        <PanelSection title="Antarctica Relationship">
          <div className="space-y-2 text-[12px] mb-3">
            {[
              ['Deals with Antarctica', String(sponsor.dealsWithAntarctica)],
              ['Total Volume', sponsor.totalVolume],
              ['Revenue Earned', sponsor.revenueEarned],
              ['Avg DSCR at Orig', sponsor.avgDscrAtOrig],
              ['Default History', sponsor.defaultHistory],
              ['Avg Days to Close', sponsor.avgDaysToClose > 0 ? String(sponsor.avgDaysToClose) : 'N/A'],
              ['First Deal', sponsor.firstDeal],
              ['Relationship Tier', sponsor.relationshipTier],
            ].map(([label, val]) => (
              <div key={label} className="flex justify-between">
                <span className="text-stone-500">{label}</span>
                <span className={`text-stone-200 font-mono ${label === 'Relationship Tier' ? 'text-amber-300 font-semibold' : ''}`}>{val}</span>
              </div>
            ))}
          </div>

          <div className="text-[10px] font-mono text-stone-500 uppercase tracking-wider mb-1.5">Deal History</div>
          <table className="dense-table">
            <thead>
              <tr>
                <th>Deal</th>
                <th>Property</th>
                <th className="text-right">Amount</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {sponsor.dealHistory.map((d) => (
                <tr key={d.dealId}>
                  <td className="font-mono">{d.dealId}</td>
                  <td className="truncate max-w-[120px]">{d.property}</td>
                  <td className="text-right font-mono">{d.amount}</td>
                  <td>{d.date}</td>
                  <td>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      d.status === 'Performing' ? 'bg-emerald-400/10 text-emerald-400' :
                      d.status === 'Paid Off' ? 'bg-blue-400/10 text-blue-400' :
                      'bg-amber-400/10 text-amber-300'
                    }`}>{d.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </PanelSection>

        <PanelSection title="Risk Assessment">
          <div className="text-[10px] font-mono text-stone-500 uppercase tracking-wider mb-2">Risk Factors</div>
          <div className="space-y-2 mb-4">
            {sponsor.riskFactors.map((rf) => (
              <div key={rf.factor} className="flex justify-between items-center text-[12px]">
                <span className="text-stone-400">{rf.factor}</span>
                <RiskBadge level={rf.level} />
              </div>
            ))}
          </div>
          <div className="h-px bg-amber-400/10 my-2" />
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[12px] text-stone-400">Overall Risk Rating:</span>
            <span className={`text-[13px] font-bold ${
              sponsor.overallRiskRating === 'LOW' ? 'text-emerald-400' :
              sponsor.overallRiskRating === 'MODERATE' ? 'text-amber-400' :
              'text-red-400'
            }`}>{sponsor.overallRiskRating}</span>
          </div>
          {sponsor.overallRiskScore > 0 && <ScoreBar score={sponsor.overallRiskScore} />}
          <p className="text-[11px] text-stone-500 mt-3 leading-relaxed">
            <span className="text-stone-400 font-medium">Concentration Note:</span> {sponsor.concentrationNote}
          </p>
        </PanelSection>
      </div>

      {/* ── PORTFOLIO OVERVIEW ─────────────────────────── */}
      <PanelSection title="Portfolio Overview">
        <div className="max-h-[240px] overflow-y-auto">
          <table className="dense-table">
            <thead className="sticky top-0 z-10 bg-black/80">
              <tr>
                <th>Property</th>
                <th>Type</th>
                <th>Location</th>
                <th className="text-right">Units/SF</th>
                <th className="text-right">Value</th>
                <th className="text-right">Year</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {sponsor.portfolio.map((p) => (
                <tr key={p.name}>
                  <td className="text-stone-200">{p.name}</td>
                  <td>{p.type}</td>
                  <td>{p.location}</td>
                  <td className="text-right font-mono">{p.units ? p.units.toLocaleString() : p.sf ? p.sf.toLocaleString() : '--'}</td>
                  <td className="text-right font-mono">{p.value}</td>
                  <td className="text-right font-mono">{p.year}</td>
                  <td>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      p.status === 'Stabilized' ? 'bg-emerald-400/10 text-emerald-400' :
                      p.status === 'Lease-Up' ? 'bg-amber-400/10 text-amber-300' :
                      'bg-blue-400/10 text-blue-400'
                    }`}>{p.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 pt-2 border-t border-amber-400/10 space-y-1 text-[12px]">
          <p><span className="text-stone-500">Portfolio Metrics:</span> <span className="text-stone-200">{sponsor.portfolioMetrics}</span></p>
          <p><span className="text-stone-500">Geo:</span> <span className="text-stone-200">{sponsor.geoBreakdown}</span></p>
          <p><span className="text-stone-500">Type:</span> <span className="text-stone-200">{sponsor.typeBreakdown}</span></p>
        </div>
      </PanelSection>

      {/* ── PRINCIPALS + NEWS ────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3">
        <PanelSection title="Key Principals">
          <div className="space-y-4">
            {sponsor.principals.map((p) => (
              <div key={p.name} className="text-[12px]">
                <div className="text-stone-100 font-semibold">{p.name} — {p.title}</div>
                <div className="text-stone-400 mt-0.5">{p.experience}</div>
                <div className="text-stone-500 mt-0.5">Previously: {p.previous.join(', ')}</div>
                <div className="text-stone-500">Education: {p.education}</div>
                <div className="text-stone-500">Board: {p.boards.join(', ')}</div>
              </div>
            ))}
          </div>
        </PanelSection>

        <PanelSection title="News & Press">
          <div className="space-y-2.5">
            {sponsor.news.map((n, i) => (
              <div key={i} className="flex items-start gap-2 pb-2 border-b border-amber-400/6 last:border-0 last:pb-0">
                <Star size={10} className="text-amber-400/50 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-[12px] text-stone-200 leading-snug">{n.headline}</p>
                  <span className="text-[10px] text-stone-500">{n.timeAgo}</span>
                </div>
              </div>
            ))}
          </div>
        </PanelSection>
      </div>

      {/* ── FINANCIAL STRENGTH ──────────────────────────── */}
      <PanelSection title="Financial Strength">
        <div className="flex flex-wrap gap-x-8 gap-y-1.5 text-[12px]">
          <p><span className="text-stone-500">Net Worth:</span> <span className="text-stone-200 font-medium">{sponsor.netWorth}</span></p>
          <p><span className="text-stone-500">Liquidity:</span> <span className="text-stone-200 font-medium">{sponsor.liquidity}</span></p>
          <p><span className="text-stone-500">Leverage Ratio:</span> <span className="text-stone-200">{sponsor.leverageRatio}</span></p>
          <p><span className="text-stone-500">Credit Lines:</span> <span className="text-stone-200">{sponsor.creditLines}</span></p>
          <p><span className="text-stone-500">Guarantor Capacity:</span> <span className="text-amber-300 font-medium">{sponsor.guarantorCapacity}</span></p>
          <p><span className="text-stone-500">Tax Returns:</span> <span className="text-stone-200">{sponsor.taxReturns}</span></p>
          <p><span className="text-stone-500">PFS:</span> <span className="text-stone-200">{sponsor.pfs}</span></p>
        </div>
      </PanelSection>
    </div>
  )
}

